import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Upload, X } from 'lucide-react'
import { useOrganization } from '../../hooks/useOrganization'
import { useUpdateBudget } from '../../hooks/useCosts'
import { useUpdateOrganization } from '../../hooks/useUpdateOrganization'
import { useOrgBranding } from '../../hooks/useOrgBranding'
import { useAppStore } from '../../stores/app'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { supabase } from '../../lib/supabase'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string

function getLogoPublicUrl(orgId: string, filename: string): string {
  return `${SUPABASE_URL}/storage/v1/object/public/org-assets/${orgId}/${filename}`
}

export function SettingsOrganizationSection() {
  const organizationId = useAppStore((state) => state.organizationId)
  const { org, isLoading: orgLoading, error: orgError } = useOrganization()
  const updateBudget = useUpdateBudget()
  const updateOrganization = useUpdateOrganization(organizationId)
  const updateBranding = useOrgBranding()

  const [name, setName] = useState('')
  const [budgetInput, setBudgetInput] = useState('')
  const [primaryColor, setPrimaryColor] = useState('#00D4AA')
  const [accentColor, setAccentColor] = useState('#00C896')
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const initialSynced = useRef(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (org && !initialSynced.current) {
      initialSynced.current = true
      setName(org.name ?? '')
      setBudgetInput(String(org.monthly_budget_limit ?? ''))
      setPrimaryColor(org.ui_primary_color ?? '#00D4AA')
      setAccentColor(org.ui_accent_color ?? '#00C896')
      setLogoPreview(org.logo_url ?? null)
    }
  }, [org])

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('El archivo debe ser una imagen')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('El logo no puede superar los 2 MB')
      return
    }
    setLogoFile(file)
    setLogoPreview(URL.createObjectURL(file))
  }

  const handleRemoveLogo = () => {
    setLogoFile(null)
    setLogoPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const uploadLogo = async (): Promise<string | null> => {
    if (!logoFile || !organizationId) return null
    const ext = logoFile.name.split('.').pop() ?? 'png'
    const path = `${organizationId}/logo.${ext}`
    const { error } = await supabase.storage
      .from('org-assets')
      .upload(path, logoFile, { upsert: true, contentType: logoFile.type })
    if (error) throw new Error(`Error al subir logo: ${error.message}`)
    return getLogoPublicUrl(organizationId, `logo.${ext}`)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!organizationId) return

    const budgetNum = Number(budgetInput)
    if (Number.isNaN(budgetNum) || budgetNum < 0) {
      toast.error('El presupuesto debe ser un número mayor o igual a 0')
      return
    }

    const nameChanged = (org?.name ?? '') !== name.trim()
    const budgetChanged = Number(org?.monthly_budget_limit) !== budgetNum
    const primaryChanged = (org?.ui_primary_color ?? '#00D4AA') !== primaryColor
    const accentChanged = (org?.ui_accent_color ?? '#00C896') !== accentColor
    const logoChanged = logoFile !== null || (logoPreview === null && org?.logo_url !== null)

    if (!nameChanged && !budgetChanged && !primaryChanged && !accentChanged && !logoChanged) {
      toast.info('No hay cambios que guardar')
      return
    }

    const isPending = updateOrganization.isPending || updateBudget.isPending || updateBranding.isPending || isUploadingLogo
    if (isPending) return

    try {
      if (nameChanged) {
        await updateOrganization.mutateAsync(name.trim())
      }
      if (budgetChanged) {
        await updateBudget.mutateAsync(budgetNum)
      }

      const brandingPayload: Record<string, string | null> = {}
      if (primaryChanged) brandingPayload['ui_primary_color'] = primaryColor
      if (accentChanged) brandingPayload['ui_accent_color'] = accentColor

      if (logoChanged) {
        setIsUploadingLogo(true)
        try {
          if (logoFile) {
            const publicUrl = await uploadLogo()
            brandingPayload['logo_url'] = publicUrl
          } else {
            brandingPayload['logo_url'] = null
          }
        } finally {
          setIsUploadingLogo(false)
        }
      }

      if (Object.keys(brandingPayload).length > 0) {
        await updateBranding.mutateAsync(brandingPayload)
      }

      setLogoFile(null)
      toast.success('Organización actualizada correctamente')
    } catch (err) {
      toast.error('Error al guardar', {
        description: err instanceof Error ? err.message : 'Error inesperado',
      })
    }
  }

  const isPending =
    updateOrganization.isPending ||
    updateBudget.isPending ||
    updateBranding.isPending ||
    isUploadingLogo

  if (!organizationId) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold text-slate-800 mb-2">Organización</h2>
        <p className="text-slate-500 text-sm">No hay organización asignada. Asigna una organización a tu perfil para editar estos datos.</p>
      </div>
    )
  }

  if (orgLoading) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">Organización</h2>
        <div className="flex justify-center items-center py-8">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  if (orgError || !org) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold text-slate-800 mb-2">Organización</h2>
        <p className="text-slate-500 text-sm">No se pudo cargar la organización. Vuelve a intentar más tarde.</p>
      </div>
    )
  }

  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-slate-800 mb-4">Organización</h2>
      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Name + Budget */}
        <div className="space-y-4">
          <div>
            <label htmlFor="org-name" className="label">Nombre de la organización</label>
            <input
              id="org-name"
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isPending}
            />
          </div>
          <div>
            <label htmlFor="org-budget" className="label">Presupuesto mensual (USD)</label>
            <input
              id="org-budget"
              type="number"
              min={0}
              step={0.01}
              className="input w-full max-w-[200px]"
              value={budgetInput}
              onChange={(e) => setBudgetInput(e.target.value)}
              disabled={isPending}
            />
          </div>
        </div>

        <hr className="border-slate-200" />

        {/* Branding section */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Identidad visual de la UI</h3>

          {/* Logo upload */}
          <div>
            <label className="label">Logo de la organización</label>
            <div className="flex items-center gap-4">
              {logoPreview ? (
                <div className="relative w-24 h-12 border border-slate-200 rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="max-h-10 max-w-[88px] object-contain"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="absolute top-0.5 right-0.5 bg-white rounded-full p-0.5 shadow text-slate-400 hover:text-red-500"
                    title="Eliminar logo"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="w-24 h-12 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center text-slate-400 text-xs">
                  Sin logo
                </div>
              )}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id="logo-upload"
                  onChange={handleLogoSelect}
                  disabled={isPending}
                />
                <label
                  htmlFor="logo-upload"
                  className="btn btn-secondary cursor-pointer flex items-center gap-2 text-sm"
                >
                  <Upload className="w-4 h-4" />
                  {logoPreview ? 'Cambiar logo' : 'Subir logo'}
                </label>
                <p className="text-xs text-slate-400 mt-1">PNG, SVG o JPG. Máx. 2 MB.</p>
              </div>
            </div>
          </div>

          {/* Color pickers */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="primary-color" className="label">Color primario</label>
              <div className="flex items-center gap-2">
                <input
                  id="primary-color"
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  disabled={isPending}
                  className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white"
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => {
                    const val = e.target.value
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) setPrimaryColor(val)
                  }}
                  disabled={isPending}
                  className="input w-28 font-mono text-sm uppercase"
                  maxLength={7}
                />
              </div>
            </div>
            <div>
              <label htmlFor="accent-color" className="label">Color de acento</label>
              <div className="flex items-center gap-2">
                <input
                  id="accent-color"
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  disabled={isPending}
                  className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white"
                />
                <input
                  type="text"
                  value={accentColor}
                  onChange={(e) => {
                    const val = e.target.value
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) setAccentColor(val)
                  }}
                  disabled={isPending}
                  className="input w-28 font-mono text-sm uppercase"
                  maxLength={7}
                />
              </div>
            </div>
          </div>

          {/* Live preview strip */}
          <div>
            <p className="label">Vista previa del sidebar</p>
            <div
              className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 w-fit"
              style={{ backgroundColor: `${primaryColor}12` }}
            >
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: primaryColor }}
              />
              <span className="text-sm font-medium" style={{ color: primaryColor }}>
                {name || 'Mi organización'}
              </span>
              <div
                className="w-3 h-3 rounded-full ml-2"
                style={{ backgroundColor: accentColor }}
              />
              <span className="text-xs text-slate-500">acento</span>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={isPending}
          className="btn btn-primary"
        >
          {isPending ? 'Guardando...' : 'Guardar'}
        </button>
      </form>
    </div>
  )
}
